import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

export const API_BASE_URL = "https://port-0-bemaster-mild533144fe3281.sel3.cloudtype.app/";

// 백엔드 STOMP 전용 엔드포인트 및 pub/sub 경로
const WS_ENDPOINT = `${API_BASE_URL.replace(/\/$/, "")}/stomp-ws`;
const PUB_ENDPOINTS = (roomId) => [`/pub/chat/${roomId}`];
const SUB_ENDPOINTS = (roomId) => [`/sub/chat/${roomId}`];

export default class WSClient {
    constructor({ roomId, reconnectDelay = 5000 } = {}) {
        this.roomId = roomId ?? 0;
        this.reconnectDelay = reconnectDelay;
        this.client = null;
        this.subscriptions = [];
        this.connected = false;
        this.onMessage = null; // 메시지 수신 시 실행할 콜백
        this.onConnect = null; // 연결 성공 시 실행할 콜백
        this.onDisconnect = null; // 연결 종료 시 실행할 콜백
        this.listeners = {
            open: new Set(),
            message: new Set(),
            error: new Set(),
            close: new Set(),
        };
    }

    // 연결 시작
    connect({ onMessage, onConnect, onDisconnect } = {}) {
        // [Fix] 콜백은 항상 최신으로 업데이트 (중복 연결 시에도 핸들러 갱신)
        this.onMessage = onMessage;
        this.onConnect = onConnect;
        this.onDisconnect = onDisconnect;

        if (this.client?.active) return; // 이미 활성화된 경우 중복 연결 방지

        const client = new Client({
            webSocketFactory: () => new SockJS(WS_ENDPOINT),
            reconnectDelay: this.reconnectDelay,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,

            // ... (rest of configuration)

            onConnect: (frame) => {
                console.log('[WS] Connected');
                this.connected = true;
                this._subscribe(); // 연결되면 구독 설정
                if (this.onConnect) this.onConnect(frame);
                this._emit("open");
            },

            onStompError: (frame) => {
                console.error('[WS] Broker reported error: ' + frame.headers['message']);
                console.error('[WS] Additional details: ' + frame.body);
                this._emit("error", new Error(frame?.headers?.message || "STOMP error"));
            },

            onWebSocketClose: () => {
                console.log('[WS] Closed');
                this.connected = false;
                if (this.onDisconnect) this.onDisconnect();
                this._emit("close");
            }
        });

        this.client = client;
        client.activate();
    }

    // 구독 설정 (내부용)
    _subscribe() {
        if (!this.client || !this.client.connected) return;

        // 기존 구독 해제
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];

        // 설정된 모든 엔드포인트 구독
        SUB_ENDPOINTS(this.roomId).forEach(dest => {
            const sub = this.client.subscribe(dest, (message) => {
                if (!message.body) return;
                try {
                    const payload = JSON.parse(message.body);
                    this.onMessage?.(payload);
                    this._emit("message", payload);
                } catch (e) {
                    console.error('[WS] JSON Parse Error', e);
                    const raw = { raw: message.body };
                    this.onMessage?.(raw);
                    this._emit("message", raw);
                }
            });
            this.subscriptions.push(sub);
        });
    }

    // 메시지 전송
    send(messageObj) {
        if (!this.client || !this.client.connected) {
            console.warn('[WS] Cannot send message: Client not connected');
            return;
        }

        const payload = JSON.stringify(messageObj);

        PUB_ENDPOINTS(this.roomId).forEach(dest => {
            this.client.publish({
                destination: dest,
                body: payload
            });
        });
    }

    // 연결 종료
    disconnect() {
        if (this.client) {
            this.client.deactivate();
            this.client = null; // [Fix] 클라이언트 인스턴스 초기화하여 재연결 시 새 인스턴스 생성 유도
            console.log('[WS] Disconnected by user');
        }
        this.connected = false;
        this.subscriptions = [];
        this._emit("close");
    }

    on(event, handler) {
        this.listeners[event]?.add(handler);
        return () => this.off(event, handler);
    }

    off(event, handler) {
        this.listeners[event]?.delete(handler);
    }

    _emit(event, ...args) {
        this.listeners[event]?.forEach((fn) => fn(...args));
    }

    // 메시지 객체 생성 헬퍼
    static buildTalkMessage({ text, senderName = "User", chatRoomId = 0 }) {
        // 임시 ID 생성 (서버 응답 전 UI 표시용)
        const tempId = `temp-${Date.now()}`;
        return {
            // id: undefined, // 서버에서 생성
            chatRoomId: chatRoomId,
            senderUserId: 0, // 로그인한 유저 ID (필요시 수정)
            senderName: senderName,
            receiverUserId: 0,
            receiverName: "",
            message: text,
            messageType: "TALK", // JOIN, TALK 등
            ts: new Date().toISOString(),
            clientTempId: tempId
        };
    }

    static buildImageMessage({ imageUrl, senderName = "User", chatRoomId = 0 }) {
        const tempId = `temp-${Date.now()}`;
        return {
            chatRoomId,
            senderUserId: 0,
            senderName,
            message: "사진을 보냈습니다.", // 미리보기용 텍스트
            imageUrl,
            messageType: "IMAGE",
            ts: new Date().toISOString(),
            clientTempId: tempId
        };
    }

    // AI 여행 계획 공유 메시지 (카드 형태로 표시됨)
    // 백엔드 호환성을 위해 TALK 타입으로 전송하되, 특별한 prefix로 구분
    static buildAiPlanMessage({ senderName = "User", chatRoomId = 0, planTitle, planData }) {
        const tempId = `temp-${Date.now()}`;

        // 요약 정보 생성
        const schedule = Array.isArray(planData?.schedule) ? planData.schedule : [];
        const dayCount = schedule.length;
        const placeCount = schedule.reduce((sum, day) => {
            return sum + (Array.isArray(day?.places) ? day.places.length : 0);
        }, 0);

        // planData를 JSON으로 인코딩하여 message에 포함
        // prefix "::AI_PLAN::" 로 시작하면 프론트에서 카드로 렌더링
        const encodedPlan = JSON.stringify({
            title: planTitle || planData?.title || '새로운 여행 계획',
            description: planData?.description,
            schedule: planData?.schedule,
            dayCount,
            placeCount
        });

        return {
            chatRoomId,
            senderUserId: 0,
            senderName,
            message: `::AI_PLAN::${encodedPlan}`,
            messageType: "TALK", // 백엔드 호환을 위해 TALK 사용
            ts: new Date().toISOString(),
            clientTempId: tempId
        };
    }

    // --- REST API Helpers ---

    static async fetchRooms() {
        try {
            const token = sessionStorage.getItem('token') || localStorage.getItem('accessToken');
            const headers = {
                "Content-Type": "application/json"
            };
            if (token) {
                headers["Authorization"] = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
            }

            const res = await fetch(`${API_BASE_URL}api/chats/rooms`, {
                headers: headers
            });
            if (!res.ok) throw new Error("Failed to fetch rooms");
            return await res.json();
        } catch (e) {
            console.error(e);
            return [];
        }
    }

    static async createRoom(name = "새 채팅방") {
        try {
            const res = await fetch(`${API_BASE_URL}api/chats/rooms`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, roomId: 0, id: "" }), // API 스펙에 맞춤
            });
            if (!res.ok) throw new Error("Failed to create room");
            return await res.json();
        } catch (e) {
            console.error(e);
            return null;
        }
    }

    static async fetchChats(chatRoomId) {
        try {
            const token = sessionStorage.getItem('token') || localStorage.getItem('accessToken');
            const headers = {
                "Content-Type": "application/json"
            };
            if (token) {
                headers["Authorization"] = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
            }

            const res = await fetch(`${API_BASE_URL}api/chats/${chatRoomId}`, {
                headers: headers
            });
            if (!res.ok) throw new Error("Failed to fetch chats");
            return await res.json();
        } catch (e) {
            console.error(e);
            return [];
        }
    }
}